import localforage from "localforage";
import { SerializationError, StorageQuotaError, LocalForageError } from "./types.js";
const localForageInstance = localforage.createInstance({
  description: "Costco Wholesale General Storage",
  driver: [localforage.INDEXEDDB, localforage.LOCALSTORAGE],
  name: "CW_Storage",
  storeName: "general",
  version: 1
});
const createErrorResult = (error, isStoreInitialized = true) => ({
  data: null,
  error,
  success: false,
  isStoreInitialized
});
const createSuccessResult = (data) => ({
  data,
  error: null,
  success: true,
  isStoreInitialized: true
});
function createLocalForageStore(key, initialValue) {
  const subscribers = /* @__PURE__ */ new Set();
  let currentResult = {
    data: initialValue,
    error: null,
    success: true,
    isStoreInitialized: false
  };
  const emitChange = () => {
    subscribers.forEach((callback) => callback());
  };
  const initializeStore = async () => {
    try {
      const storedValue = await localForageInstance.getItem(key);
      if (storedValue !== null) {
        currentResult = createSuccessResult(storedValue);
      } else {
        await localForageInstance.setItem(key, initialValue);
        currentResult = createSuccessResult(initialValue);
      }
    } catch {
      currentResult = createErrorResult(
        new LocalForageError("Failed to initialize storage"),
        false
      );
    } finally {
      emitChange();
    }
  };
  initializeStore();
  return {
    async clear() {
      try {
        await localForageInstance.removeItem(key);
        currentResult = createSuccessResult(initialValue);
        emitChange();
        return createSuccessResult(void 0);
      } catch (error) {
        const storageError = new LocalForageError(
          error instanceof Error ? error.message : "Failed to clear storage"
        );
        currentResult = createErrorResult(storageError);
        emitChange();
        return createErrorResult(storageError);
      }
    },
    getSnapshot() {
      return currentResult;
    },
    async setValue(newValue) {
      try {
        const serializable = JSON.stringify(newValue) !== void 0;
        if (!serializable) {
          throw new SerializationError();
        }
        await localForageInstance.setItem(key, newValue);
        currentResult = createSuccessResult(newValue);
        emitChange();
        return currentResult;
      } catch (error) {
        let storageError;
        if (error instanceof SerializationError) {
          storageError = error;
        } else if (error instanceof DOMException && error.name === "QuotaExceededError") {
          storageError = new StorageQuotaError();
        } else {
          storageError = new LocalForageError(
            error instanceof Error ? error.message : "Unknown storage error"
          );
        }
        currentResult = createErrorResult(storageError);
        emitChange();
        return currentResult;
      }
    },
    subscribe(callback) {
      subscribers.add(callback);
      return () => {
        subscribers.delete(callback);
      };
    }
  };
}
export {
  createLocalForageStore
};
